import { AvailabilityStatus, Benefit, FreeShippingPriceBasis, Listing, ListingFreshness, ListingFreshnessConfidence, ListingFreshnessStatus, PriceSnapshot, PriceUnknownComponent, PriceVerificationStatus, SourceStatus } from './domain';

export interface PublicPriceInput {
    listed_price?: number | null;
    sale_price?: number | null;
    instant_discount?: number | null;
    public_coupon_discount?: number | null;
    base_shipping_fee?: number | null;
    shipping_fee?: number | null;
    free_shipping_condition?: string | null;
    free_shipping_price_basis?: FreeShippingPriceBasis | null;
    price_verification_status?: PriceVerificationStatus | null;
    unknown_price_components?: PriceUnknownComponent[] | null;
    availability_status?: AvailabilityStatus | null;
    benefits?: Benefit[];
    basePrice?: number | null;
    salePrice?: number | null;
    instantDiscount?: number | null;
    publicCoupon?: number | null;
    shippingFee?: number | null;
    freeShippingThreshold?: number | string | null;
    freeShippingPriceBasis?: FreeShippingPriceBasis | null;
    priceVerificationStatus?: PriceVerificationStatus | null;
    unknownPriceComponents?: PriceUnknownComponent[] | null;
    excludedFromLowest?: boolean | null;
    excludeFromLowest?: boolean | null;
    optionPriceDelta?: number | null;
    stockStatus?: AvailabilityStatus | null;
    personalizedBenefit?: Benefit[];
    excludedBenefit?: Benefit[];
}

export interface ListingFreshnessInput {
    status?: ListingFreshnessStatus | null;
    checkedAt?: string | null;
    checked_at?: string | null;
    collectedAt?: string | null;
    collected_at?: string | null;
    cachedAt?: string | null;
    cached_at?: string | null;
    refreshInterval?: number | string | null;
    refresh_interval?: number | string | null;
    nextRefreshAt?: string | null;
    next_refresh_at?: string | null;
    failureReason?: string | null;
    failure_reason?: string | null;
    confidence?: ListingFreshnessConfidence | null;
    confidenceScore?: number | string | null;
    confidence_score?: number | string | null;
    sourceStatus?: SourceStatus | null;
    source_status?: SourceStatus | null;
    last_checked_at?: string | null;
}

export type PriceBreakdownChipKind = 'base' | 'discount' | 'coupon' | 'shipping' | 'option' | 'stock' | 'excluded';

export interface PriceBreakdownChip {
    id: string;
    label: string;
    amount: number | null;
    kind: PriceBreakdownChipKind;
    includedInEffectivePrice: boolean;
}

export interface PublicPriceBreakdown {
    listed_price: number;
    sale_price: number;
    instant_discount: number;
    public_coupon_discount: number;
    base_shipping_fee: number;
    shipping_fee: number;
    free_shipping_condition: string | null;
    public_effective_price: number;
    price_verification_status: PriceVerificationStatus;
    needs_verification: boolean;
    unknown_components: PriceUnknownComponent[];
    basePrice: number;
    salePrice: number;
    instantDiscount: number;
    publicCoupon: number;
    baseShippingFee: number;
    shippingFee: number;
    freeShippingThreshold: number | null;
    freeShippingPriceBasis: FreeShippingPriceBasis;
    optionPriceDelta: number;
    stockStatus: AvailabilityStatus | null;
    publicCouponAppliedPrice: number;
    shippingIncludedPrice: number;
    priceVerificationStatus: PriceVerificationStatus;
    needsVerification: boolean;
    unknownComponents: PriceUnknownComponent[];
    purchasable: boolean;
    excludedFromLowest: boolean;
    publicBenefit: Benefit[];
    personalizedBenefit: Benefit[];
    excludedBenefit: Benefit[];
    chips: PriceBreakdownChip[];
    formulaLabel: string;
}

export interface EffectivePriceMismatch {
    id: string;
    expected_price: number;
    actual_price: number;
}

export interface DailyLowestEffectivePriceSnapshot<T extends PriceSnapshot = PriceSnapshot> {
    dateKey: string;
    snapshot: T;
    sourceCount: number;
    public_effective_price: number;
}

const PURCHASABLE_STATUSES: AvailabilityStatus[] = ['in_stock', 'low_stock'];
const PRICE_TREND_COMPARABLE_STATUSES: AvailabilityStatus[] = ['in_stock', 'low_stock'];
const LOWEST_COMPARABLE_FRESHNESS_STATUSES: ListingFreshnessStatus[] = ['fresh', 'stale'];
const PERSONALIZED_BENEFIT_TYPES: Benefit['benefit_type'][] = ['point', 'membership', 'card', 'new_user'];
export const DEFAULT_LISTING_REFRESH_INTERVAL_MINUTES = 180;
const PRICE_UNKNOWN_COMPONENTS: PriceUnknownComponent[] = [
    'listed_price',
    'sale_price',
    'instant_discount',
    'public_coupon',
    'shipping_fee',
    'free_shipping_condition',
    'availability',
    'option_stock',
    'size_stock'
];

function numericValue(value: number | string | null | undefined): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const parsed = Number(value.replace(/,/g, '').trim());
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function stringValue(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function isoAfterMinutes(value: string | null, minutes: number): string | null {
    if (!value) return null;
    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return null;
    return new Date(timestamp + (minutes * 60 * 1000)).toISOString();
}

function firstNumericValue(values: Array<number | string | null | undefined>): number | null {
    for (const value of values) {
        const parsed = numericValue(value);
        if (parsed !== null) return parsed;
    }
    return null;
}

function positiveAmount(value: number | string | null | undefined): number {
    const parsed = numericValue(value);
    if (parsed === null) return 0;
    return Math.max(0, Math.round(parsed));
}

function signedAmount(value: number | string | null | undefined): number {
    const parsed = numericValue(value);
    if (parsed === null) return 0;
    return Math.round(parsed);
}

function thresholdFromCondition(condition: string | null | undefined): number | null {
    if (!condition) return null;
    const normalized = condition.replace(/,/g, '').trim();
    const manWonMatch = normalized.match(/(\d+(?:\.\d+)?)\s*만\s*원/);
    if (manWonMatch) return Math.round(Number(manWonMatch[1]) * 10000);

    const wonMatch = normalized.match(/(\d{4,})\s*원/);
    if (wonMatch) return Math.round(Number(wonMatch[1]));

    return null;
}

function normalizeFreeShippingThreshold(input: PublicPriceInput): number | null {
    const explicitThreshold = firstNumericValue([input.freeShippingThreshold]);
    if (explicitThreshold !== null) return Math.max(0, Math.round(explicitThreshold));
    return thresholdFromCondition(input.free_shipping_condition);
}

function normalizeFreeShippingPriceBasis(input: PublicPriceInput): FreeShippingPriceBasis {
    return input.freeShippingPriceBasis || input.free_shipping_price_basis || 'before_public_coupon';
}

function isPurchasableStatus(status: AvailabilityStatus | null): boolean {
    if (!status) return true;
    return PURCHASABLE_STATUSES.includes(status);
}

function normalizeUnknownComponents(input: PublicPriceInput): PriceUnknownComponent[] {
    const values = [
        ...(input.unknownPriceComponents || []),
        ...(input.unknown_price_components || [])
    ];
    return Array.from(new Set(values.filter((value): value is PriceUnknownComponent => (
        PRICE_UNKNOWN_COMPONENTS.includes(value as PriceUnknownComponent)
    ))));
}

function normalizePriceVerificationStatus(
    input: PublicPriceInput,
    unknownComponents: PriceUnknownComponent[]
): PriceVerificationStatus {
    if (input.priceVerificationStatus === 'needs_verification' || input.price_verification_status === 'needs_verification') {
        return 'needs_verification';
    }
    return unknownComponents.length > 0 ? 'needs_verification' : 'verified';
}

export function normalizeListingFreshness(
    input: ListingFreshnessInput,
    now: Date = new Date()
): ListingFreshness {
    const checkedAt = stringValue(input.checkedAt) || stringValue(input.checked_at) || stringValue(input.last_checked_at);
    const collectedAt = stringValue(input.collectedAt) || stringValue(input.collected_at) || checkedAt;
    const cachedAt = stringValue(input.cachedAt) || stringValue(input.cached_at);
    const refreshInterval = Math.max(
        1,
        Math.round(
            firstNumericValue([input.refreshInterval, input.refresh_interval]) ??
            DEFAULT_LISTING_REFRESH_INTERVAL_MINUTES
        )
    );
    const nextRefreshAt = stringValue(input.nextRefreshAt) || stringValue(input.next_refresh_at) || isoAfterMinutes(checkedAt, refreshInterval);
    const failureReason = stringValue(input.failureReason) || stringValue(input.failure_reason);
    const sourceStatus = input.sourceStatus || input.source_status || 'mock';
    const explicitStatus = input.status || null;
    const confidenceScore = Math.max(0, Math.min(1, firstNumericValue([input.confidenceScore, input.confidence_score]) ?? 0.5));
    const confidence = input.confidence || confidenceLabelFromScore(confidenceScore);
    const status = explicitStatus || inferListingFreshnessStatus({
        checkedAt,
        nextRefreshAt,
        failureReason,
        sourceStatus,
        refreshInterval,
        now
    });

    return {
        status,
        checkedAt,
        collectedAt,
        cachedAt,
        refreshInterval,
        nextRefreshAt,
        failureReason,
        confidence,
        confidenceScore,
        sourceStatus,

        checked_at: checkedAt,
        collected_at: collectedAt,
        cached_at: cachedAt,
        refresh_interval: refreshInterval,
        next_refresh_at: nextRefreshAt,
        failure_reason: failureReason,
        confidence_score: confidenceScore,
        source_status: sourceStatus
    };
}

function confidenceLabelFromScore(score: number): ListingFreshnessConfidence {
    if (score >= 0.85) return 'high';
    if (score >= 0.65) return 'medium';
    if (score > 0) return 'low';
    return 'unknown';
}

function inferListingFreshnessStatus(input: {
    checkedAt: string | null;
    nextRefreshAt: string | null;
    failureReason: string | null;
    sourceStatus: SourceStatus;
    refreshInterval: number;
    now: Date;
}): ListingFreshnessStatus {
    if (input.failureReason) return 'failed';
    if (input.sourceStatus === 'api_pending') return 'api_pending';

    const nowTime = input.now.getTime();
    const nextRefreshTime = input.nextRefreshAt ? new Date(input.nextRefreshAt).getTime() : NaN;
    const checkedTime = input.checkedAt ? new Date(input.checkedAt).getTime() : NaN;

    if (!Number.isFinite(checkedTime)) return 'api_pending';
    if (!Number.isFinite(nowTime) || !Number.isFinite(nextRefreshTime)) return 'stale';
    if (nowTime <= nextRefreshTime) return 'fresh';

    const expirationTime = nextRefreshTime + (input.refreshInterval * 60 * 1000);
    return nowTime <= expirationTime ? 'stale' : 'expired';
}

function formatWon(value: number): string {
    return `${new Intl.NumberFormat('ko-KR').format(Math.abs(value))}원`;
}

function benefitKey(benefit: Benefit): string {
    return benefit.id || `${benefit.benefit_type}:${benefit.name}:${benefit.amount ?? ''}:${benefit.conditions}`;
}

function uniqueBenefits(items: Benefit[]): Benefit[] {
    const seen = new Set<string>();
    const result: Benefit[] = [];

    items.forEach((item) => {
        const key = benefitKey(item);
        if (seen.has(key)) return;
        seen.add(key);
        result.push(item);
    });

    return result;
}

function publicBenefits(input: PublicPriceInput): Benefit[] {
    return (input.benefits || []).filter((benefit) => benefit.is_public_default);
}

function excludedBenefits(input: PublicPriceInput): Benefit[] {
    return uniqueBenefits([
        ...(input.excludedBenefit || []),
        ...(input.personalizedBenefit || []),
        ...(input.benefits || []).filter((benefit) => !benefit.is_public_default)
    ]);
}

function personalizedBenefits(input: PublicPriceInput): Benefit[] {
    return uniqueBenefits([
        ...(input.personalizedBenefit || []),
        ...(input.benefits || []).filter((benefit) => (
            !benefit.is_public_default &&
            PERSONALIZED_BENEFIT_TYPES.includes(benefit.benefit_type)
        ))
    ]);
}

function createBreakdownChips(breakdown: Omit<PublicPriceBreakdown, 'chips'>): PriceBreakdownChip[] {
    const chips: PriceBreakdownChip[] = [
        {
            id: 'basePrice',
            label: breakdown.unknownComponents.includes('listed_price')
                ? '상품가 확인 필요'
                : `상품가 ${formatWon(breakdown.basePrice)}`,
            amount: breakdown.unknownComponents.includes('listed_price') ? null : breakdown.basePrice,
            kind: 'base',
            includedInEffectivePrice: !breakdown.unknownComponents.includes('listed_price')
        }
    ];

    if (breakdown.optionPriceDelta !== 0) {
        const sign = breakdown.optionPriceDelta > 0 ? '+' : '-';
        chips.push({
            id: 'optionPriceDelta',
            label: `옵션가 ${sign}${formatWon(breakdown.optionPriceDelta)}`,
            amount: breakdown.optionPriceDelta,
            kind: 'option',
            includedInEffectivePrice: true
        });
    }

    if (breakdown.instantDiscount > 0) {
        chips.push({
            id: 'instantDiscount',
            label: `즉시 할인 -${formatWon(breakdown.instantDiscount)}`,
            amount: breakdown.instantDiscount,
            kind: 'discount',
            includedInEffectivePrice: true
        });
    }

    if (breakdown.publicCoupon > 0) {
        chips.push({
            id: 'publicCoupon',
            label: `공개 쿠폰 -${formatWon(breakdown.publicCoupon)}`,
            amount: breakdown.publicCoupon,
            kind: 'coupon',
            includedInEffectivePrice: true
        });
    } else if (breakdown.unknownComponents.includes('public_coupon')) {
        chips.push({
            id: 'publicCouponUnknown',
            label: '쿠폰 확인 필요',
            amount: null,
            kind: 'coupon',
            includedInEffectivePrice: false
        });
    }

    chips.push({
        id: 'shippingFee',
        label: breakdown.unknownComponents.includes('shipping_fee')
            ? '배송비 확인 필요'
            : breakdown.shippingFee > 0
                ? `배송비 +${formatWon(breakdown.shippingFee)}`
                : '무료배송',
        amount: breakdown.unknownComponents.includes('shipping_fee') ? null : breakdown.shippingFee,
        kind: 'shipping',
        includedInEffectivePrice: !breakdown.unknownComponents.includes('shipping_fee')
    });

    if (breakdown.unknownComponents.includes('option_stock') || breakdown.unknownComponents.includes('size_stock')) {
        chips.push({
            id: 'optionStockUnknown',
            label: '사이즈/재고 확인 필요',
            amount: null,
            kind: 'stock',
            includedInEffectivePrice: false
        });
    }

    if (breakdown.excludedFromLowest) {
        chips.push({
            id: 'stockStatus',
            label: '구매 불가 최저가 제외',
            amount: null,
            kind: 'stock',
            includedInEffectivePrice: false
        });
    }

    if (breakdown.excludedBenefit.length > 0) {
        chips.push({
            id: 'excludedBenefit',
            label: '개인 혜택 제외',
            amount: null,
            kind: 'excluded',
            includedInEffectivePrice: false
        });
    }

    if (breakdown.needsVerification) {
        chips.push({
            id: 'priceVerification',
            label: '가격 확인 필요',
            amount: null,
            kind: 'excluded',
            includedInEffectivePrice: false
        });
    }

    return chips;
}

export function calculatePublicEffectivePrice(input: PublicPriceInput): number {
    return getPublicPriceBreakdown(input).public_effective_price;
}

export function calculatePublicCouponAppliedPrice(input: PublicPriceInput): number {
    return getPublicPriceBreakdown(input).publicCouponAppliedPrice;
}

export function calculateShippingIncludedPrice(input: PublicPriceInput): number {
    return getPublicPriceBreakdown(input).shippingIncludedPrice;
}

export function getPublicPriceBreakdown(input: PublicPriceInput): PublicPriceBreakdown {
    const basePrice = positiveAmount(firstNumericValue([input.basePrice, input.listed_price]));
    const optionPriceDelta = signedAmount(input.optionPriceDelta);
    const optionAdjustedBasePrice = Math.max(0, basePrice + optionPriceDelta);
    const rawSalePrice = firstNumericValue([input.salePrice, input.sale_price]);
    const inputInstantDiscount = positiveAmount(firstNumericValue([input.instantDiscount, input.instant_discount]));
    const salePriceFromDiscount = Math.max(0, optionAdjustedBasePrice - inputInstantDiscount);
    const explicitSalePrice = rawSalePrice !== null ? Math.max(0, Math.round(rawSalePrice) + optionPriceDelta) : null;
    const salePrice = explicitSalePrice !== null ? Math.min(explicitSalePrice, salePriceFromDiscount) : salePriceFromDiscount;
    const instantDiscount = Math.max(0, optionAdjustedBasePrice - salePrice);
    const publicCoupon = Math.min(
        salePrice,
        positiveAmount(firstNumericValue([input.publicCoupon, input.public_coupon_discount]))
    );
    const baseShippingFee = positiveAmount(firstNumericValue([input.shippingFee, input.shipping_fee, input.base_shipping_fee]));
    const freeShippingThreshold = normalizeFreeShippingThreshold(input);
    const freeShippingPriceBasis = normalizeFreeShippingPriceBasis(input);
    const publicCouponAppliedPrice = Math.max(0, salePrice - publicCoupon);
    const freeShippingComparisonPrice = freeShippingPriceBasis === 'after_public_coupon' ? publicCouponAppliedPrice : salePrice;
    const shippingFee = freeShippingThreshold !== null && freeShippingComparisonPrice >= freeShippingThreshold ? 0 : baseShippingFee;
    const shippingIncludedPrice = publicCouponAppliedPrice + shippingFee;
    const stockStatus = input.stockStatus || input.availability_status || null;
    const purchasable = isPurchasableStatus(stockStatus);
    const unknownComponents = normalizeUnknownComponents(input);
    const priceVerificationStatus = normalizePriceVerificationStatus(input, unknownComponents);
    const needsVerification = priceVerificationStatus === 'needs_verification';
    const explicitlyExcluded = Boolean(input.excludedFromLowest || input.excludeFromLowest);
    const partialBreakdown: Omit<PublicPriceBreakdown, 'chips'> = {
        listed_price: basePrice,
        sale_price: salePrice,
        instant_discount: instantDiscount,
        public_coupon_discount: publicCoupon,
        base_shipping_fee: baseShippingFee,
        shipping_fee: shippingFee,
        free_shipping_condition: input.free_shipping_condition || null,
        public_effective_price: shippingIncludedPrice,
        price_verification_status: priceVerificationStatus,
        needs_verification: needsVerification,
        unknown_components: unknownComponents,
        basePrice,
        salePrice,
        instantDiscount,
        publicCoupon,
        baseShippingFee,
        shippingFee,
        freeShippingThreshold,
        freeShippingPriceBasis,
        optionPriceDelta,
        stockStatus,
        publicCouponAppliedPrice,
        shippingIncludedPrice,
        priceVerificationStatus,
        needsVerification,
        unknownComponents,
        purchasable,
        excludedFromLowest: !purchasable || explicitlyExcluded,
        publicBenefit: publicBenefits(input),
        personalizedBenefit: personalizedBenefits(input),
        excludedBenefit: excludedBenefits(input),
        formulaLabel: needsVerification
            ? '상품가 후보 - 확인된 공개 혜택 + 확인된 배송비'
            : '상품가 - 즉시 할인 - 공개 쿠폰 + 기본 배송비'
    };

    return {
        ...partialBreakdown,
        chips: createBreakdownChips(partialBreakdown)
    };
}

export function isPurchasableListing(listing: Listing): boolean {
    return isPurchasableStatus(listing.availability_status) && isComparableListingFreshness(listing.freshness);
}

export function isComparablePriceSnapshot(snapshot: PriceSnapshot): boolean {
    return PRICE_TREND_COMPARABLE_STATUSES.includes(snapshot.availability_status) &&
        isComparableListingFreshness(snapshot.freshness || snapshot.listing_freshness);
}

export function isComparableListingFreshness(freshness: ListingFreshness | null | undefined): boolean {
    if (!freshness) return true;
    return LOWEST_COMPARABLE_FRESHNESS_STATUSES.includes(freshness.status);
}

export function dateKeyFromPriceTimestamp(value: string): string {
    const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matched) return `${matched[1]}-${matched[2]}-${matched[3]}`;

    const timestamp = new Date(value).getTime();
    if (!Number.isFinite(timestamp)) return value;

    const seoulDate = new Date(timestamp + (9 * 60 * 60 * 1000));
    const year = seoulDate.getUTCFullYear();
    const month = `${seoulDate.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${seoulDate.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function getLowestEffectivePriceListing<T extends Listing>(listings: T[], includeUnavailable = false): T | null {
    const candidates = listings.filter((listing) => includeUnavailable || isPurchasableListing(listing));
    if (candidates.length === 0) return null;

    return [...candidates].sort((a, b) => {
        const priceDiff = calculatePublicEffectivePrice(a) - calculatePublicEffectivePrice(b);
        if (priceDiff !== 0) return priceDiff;
        return new Date(b.last_checked_at).getTime() - new Date(a.last_checked_at).getTime();
    })[0];
}

export function getDailyLowestEffectivePriceSnapshots<T extends PriceSnapshot>(snapshots: T[]): DailyLowestEffectivePriceSnapshot<T>[] {
    const grouped = new Map<string, T[]>();

    snapshots
        .filter((snapshot) => isComparablePriceSnapshot(snapshot))
        .forEach((snapshot) => {
            const dateKey = dateKeyFromPriceTimestamp(snapshot.checked_at);
            const items = grouped.get(dateKey) || [];
            items.push(snapshot);
            grouped.set(dateKey, items);
        });

    return Array.from(grouped.keys()).sort().map((dateKey) => {
        const dailySnapshots = grouped.get(dateKey) || [];
        const lowestSnapshot = [...dailySnapshots].sort((a, b) => {
            const priceDiff = calculatePublicEffectivePrice(a) - calculatePublicEffectivePrice(b);
            if (priceDiff !== 0) return priceDiff;
            return new Date(b.checked_at).getTime() - new Date(a.checked_at).getTime();
        })[0];

        return {
            dateKey,
            snapshot: lowestSnapshot,
            sourceCount: dailySnapshots.length,
            public_effective_price: calculatePublicEffectivePrice(lowestSnapshot)
        };
    });
}

export function validateListingEffectivePrices(listings: Listing[]): EffectivePriceMismatch[] {
    return listings
        .map((listing) => ({
            id: listing.id,
            expected_price: calculatePublicEffectivePrice(listing),
            actual_price: listing.public_effective_price
        }))
        .filter((item) => item.expected_price !== item.actual_price);
}

export function validatePriceSnapshotEffectivePrices(snapshots: PriceSnapshot[]): EffectivePriceMismatch[] {
    return snapshots
        .map((snapshot) => ({
            id: snapshot.id,
            expected_price: calculatePublicEffectivePrice(snapshot),
            actual_price: snapshot.public_effective_price
        }))
        .filter((item) => item.expected_price !== item.actual_price);
}
